import {SnapGeo, StickMaterial} from "./StickMaterial";
import {FrameBlockExt, InteriorStick, cellHasDoor} from "./FrameBlockExt";
import {assertNotNaN, makeMullionResize, makeMullionThicknessAbsorb, stickThicknessVar} from "./Util";
import {FrameGridView} from "./FrameGridScene";
import {MdFrameBlock, MdFrameStick} from "../../model/frame/MdFrameBlock";
import {FrameBlockColumn} from "./FrameBlockColumn";
import {FrameBlockItem} from "./FrameBlockItem";
import {absorbSizeDelta} from "./BlockOps";
import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";

export class FrameBlockRow extends FrameBlockExt {

    // vertical mullions between adjacent cells. interior[i] sits between
    // children[i] and children[i+1]; undefined when the model declares no stick
    // there. These consume flow width (the row grows by their thickness).
    private interior: (InteriorStick | undefined)[] = [];

    constructor(scene: FrameGridView, model: MdFrameBlock, parent?: FrameBlockRow|FrameBlockColumn) {
        super(scene, model, parent);

        const spec = this.elev.frameSpec;

        // walk children, separating cells from sticks. A stick between two cells is
        // an interior mullion; a stick before the first / after the last cell is the
        // row's own edge stick (a jamb on the outline), drawn by the root's
        // renderOutline and given room here.
        let pendingGap: MdFrameStick | undefined;
        this.model.children.forEach(c => {
            if(c.kind === "stick") {
                if(this.children.length === 0) this.leadingEdgeStick = c.stick;
                else pendingGap = c.stick;
                return;
            }

            // a cell — first close any pending mullion against the previous cell
            if(this.children.length > 0) {
                this.interior[this.children.length - 1] = pendingGap ? this.makeInterior(pendingGap, spec) : undefined;
            }
            pendingGap = undefined;

            if(c.kind === "item") {
                const item = new FrameBlockItem(this.scene, c.item, this);
                this.children.push(item);
                this.scene.parentThing.add(item);
            } else if(c.block.kind === "column") {
                this.children.push(new FrameBlockColumn(this.scene, c.block, this));
            } else if(c.block.kind === "row") {
                this.children.push(new FrameBlockRow(this.scene, c.block, this));
            }
        });

        // a stick still pending after the last cell is the trailing edge stick
        if(pendingGap) this.trailingEdgeStick = pendingGap;
    }

    private makeInterior(stickModel: MdFrameStick, spec: MdFrameSpec): InteriorStick {
        const length = new FrmVar(`${stickModel.id}:len`, 0);
        const thickness = stickThicknessVar(stickModel, spec);

        // this mullion divides the cell just pushed (left) from the one about to be
        // pushed next (right). Capture their indices and resolve lazily, since the
        // right cell doesn't exist yet. Dragging it reallocates width between them.
        const leftIdx = this.children.length - 1;
        const rightIdx = this.children.length;

        // each piece of the mullion (see splitAtVoids) drags and edits as the whole.
        const build = (length: FrmVar, id: string) => new StickMaterial(this.elev, {
            id,
            stickId: stickModel.id,
            model: stickModel,
            view: this.scene,
            orientation: "vertical",
            length,
            thickness,
            resize: makeMullionResize({
                leading: () => this.children[leftIdx].width!,
                trailing: () => this.children[rightIdx].width!,
                relayout: () => this.relayout(),
                snapsUnder: side => this.isStep(side === "leading" ? leftIdx : rightIdx),
                snapUnder: side => this.snapStepToEdge(side === "leading" ? leftIdx : rightIdx),
                status: s => this.elev.dragStatus.emit(s),
                cellsUnderMin: () => this.cellsUnderMin(),
                // a resized neighbour block scales its contents with it.
                onResize: (side, from, to) => {
                    const cell = this.children[side === "leading" ? leftIdx : rightIdx];
                    if(cell instanceof FrameBlockExt) absorbSizeDelta(spec, cell.model, "width", to - from);
                },
            }),
            absorbThickness: makeMullionThicknessAbsorb({
                leading: () => this.absorbTarget(leftIdx),
                trailing: () => this.absorbTarget(rightIdx),
            }),
            centerSnap: () => this.centerSnapGeo(),
        });
        const add = (mat: StickMaterial) => {
            this.scene.parentThing.add(mat);
            this.scene.registerStick(mat);
            return mat;
        };

        const stick = add(build(length, stickModel.id));
        const parts: InteriorStick["parts"] = [];
        // a split-off piece selects on its own, so it gets its own id (see pieceStickId).
        const makePart = () => {
            const id = `${stickModel.id}#${parts.length}`;
            const partLength = new FrmVar(`${id}:len`, 0);
            return {mat: add(build(partLength, id)), length: partLength};
        };
        return {stick, length, thickness, parts, makePart};
    }

    // this row's own vertical centre-line, offered as a snap target while its
    // mullion is dragged so the stick can be dropped exactly on centre. Only when
    // the row holds a single mullion — with more than one there's no one stick the
    // centre belongs to — and only once the row has been laid out.
    private centerSnapGeo(): SnapGeo | null {
        if(this.interior.filter(s => !!s).length !== 1) return null;

        const r = this.layoutRect;
        if(!r) return null;

        return {center: r.x + r.width / 2, alongMin: r.y, alongMax: r.y + r.height};
    }

    // the width var of the cell at `idx` when it can give width up to a growing
    // mullion, else null: a door-bearing cell's width is dictated by the door.
    private absorbTarget(idx: number): FrmVar | null {
        const cell = this.children[idx];
        if(!cell || cellHasDoor(cell)) return null;
        return cell.width ?? null;
    }

    // see FrameBlockExt.splitInteriorAtVoids.
    protected splitInteriorAtVoids() {
        this.interior.forEach((s, i) => {
            if(s) this.splitAtVoids(s, this.children[i], this.children[i + 1]);
        });
    }

    // total width consumed by the row's sticks: the interior mullions and its edge
    // sticks (their thicknesses are constants as far as the layout solver is concerned).
    interiorWidthTotal(): number {
        return this.edgeThicknessTotal() + this.interior.reduce((acc, s) => acc + (s ? s.thickness.get() : 0), 0);
    }

    // ref is based on the top left
    reposition(refX: number, refY: number) {
        this.setLayoutRect(refX, refY);
        const rowHeight = this.getHeight(true);

        // the leading edge stick (left jamb) takes the first slot; the root draws it.
        refX += this.edgeThickness(this.leadingEdgeStick);

        this.children.forEach((c, i) => {
            c.reposition(refX, refY);
            refX += c.getWidth(true);

            if(i < this.children.length - 1) {
                const s = this.interior[i];
                if(s) {
                    // vertical mullion fills the gap and spans the row height, short of a
                    // head or sill both neighbours carry (see interiorTrim). Beside a void
                    // it is drawn in pieces (see splitAtVoids).
                    const cross = {min: refX, max: refX + s.thickness.get()};
                    const top = this.interiorTrim(c, this.children[i + 1], "top", cross);
                    const bottom = this.interiorTrim(c, this.children[i + 1], "bottom", cross);
                    s.stick.graphic.position.set(refX, refY + top);
                    s.length.set(rowHeight - top - bottom, {debug: "stick-layout"});
                    refX += s.thickness.get();
                }
            }
        });
    }

    getWidth(strict: boolean) {
        if(this.width.isModified()) return this.width.get();

        return this.interiorWidthTotal() + this.children.reduce((acc, c) => {
            return acc + assertNotNaN(c.getWidth(strict), strict, `${this.model.id}: can't calculate width for row`)
        }, 0);
    }

    getHeight(strict: boolean) {
        if(this.height.isModified()) return this.height.get();

        for(let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const value = child.getHeight(false)
            if(!isNaN(value)) return value;
        }

        if(strict) {
            throw new Error(`${this.model.id}: can't get height for row`)
        }

        return NaN;
    }
}
